import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Clock, ChevronLeft, ChevronRight, Plus, Loader2, CopyPlus } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { EntryDialog } from '@/components/timesheets/EntryDialog';
import { DayView, WeekView, MonthView } from '@/components/timesheets/CalendarViews';
import { SummaryPanel, JobTypeManager } from '@/components/timesheets/SummaryPanel';
import { HoursDigestCard } from '@/components/timesheets/HoursDigestCard';
import { ClockCard } from '@/components/timesheets/ClockCard';
import { WorkLocations, WhosIn } from '@/components/timesheets/ClockAdmin';
import { iso, addDays, rangeFor, fmtDay, todayIso } from '@/components/timesheets/utils';

const VIEWS = [['day', 'Day'], ['week', 'Week'], ['month', 'Month']];

export default function Timesheets() {
  const { user, can } = useContext(AuthContext);
  const navigate = useNavigate();
  const isManager = ['admin', 'manager'].includes(user?.role);
  const [view, setView] = useState(() => (window.innerWidth < 768 ? 'day' : 'week'));
  const [anchor, setAnchor] = useState(new Date());
  const [personId, setPersonId] = useState('');
  const [people, setPeople] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {entry?, date}

  const [rangeStart, rangeEnd] = useMemo(() => rangeFor(view, anchor), [view, anchor]);
  const scope = isManager ? (personId || user?.user_id) : user?.user_id;
  const qs = `start=${iso(rangeStart)}&end=${iso(rangeEnd)}${isManager ? `&user_id=${personId || user?.user_id}` : ''}`;

  const loadStatic = useCallback(async () => {
    const [jt, pp] = await Promise.all([
      fetch(`${API_URL}/api/timesheets/job-types`, { credentials: 'include' }),
      fetch(`${API_URL}/api/timesheets/people`, { credentials: 'include' }),
    ]);
    if (jt.ok) setJobTypes(await jt.json());
    if (pp.ok) setPeople(await pp.json());
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([
        fetch(`${API_URL}/api/timesheets/entries?${qs}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/timesheets/summary?${qs}`, { credentials: 'include' }),
      ]);
      if (!e.ok) throw new Error('Could not load timesheets');
      setEntries(await e.json());
      if (s.ok) setSummary(await s.json());
    } catch (err) { toast.error(err.message); } finally { setLoading(false); }
  }, [qs]);
  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { load(); }, [load]);

  const step = (n) => setAnchor((a) => (view === 'month' ? new Date(a.getFullYear(), a.getMonth() + n, 1) : addDays(a, view === 'week' ? 7 * n : n)));
  const title = view === 'month' ? anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : view === 'week' ? `${fmtDay(rangeStart, { day: 'numeric', month: 'short' })} – ${fmtDay(rangeEnd, { day: 'numeric', month: 'short', year: 'numeric' })}`
      : fmtDay(anchor, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const showPerson = isManager && personId === 'all';
  const onSaved = () => load();
  const copyPrev = async (toDate) => {
    const fromDate = iso(addDays(new Date(`${toDate}T00:00:00`), -1));
    try {
      const r = await fetch(`${API_URL}/api/timesheets/entries/copy-day`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_date: fromDate, to_date: toDate, user_id: isManager && personId && personId !== 'all' ? personId : undefined }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Copy failed');
      toast.success(d.copied ? `Copied ${d.copied} entr${d.copied === 1 ? 'y' : 'ies'} from ${fmtDay(new Date(`${fromDate}T00:00:00`))}` : 'Already copied — nothing new to add');
      load();
    } catch (e) { toast.error(e.message); }
  };
  const onDeleted = (id) => setEntries((cur) => cur.filter((e) => e.id !== id)) || load();

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white overflow-x-hidden">
      <div className="text-white bg-[#1f2937]">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3 flex-wrap">
          <button onClick={() => navigate('/staff')} className="p-2 hover:bg-white/10 rounded-lg" aria-label="Back" data-testid="ts-back"><ArrowLeft className="w-5 h-5" /></button>
          <Link to="/" className="text-white/70 hover:text-white hidden sm:inline-flex"><Home className="w-5 h-5" /></Link>
          <div className="w-8 h-8 rounded-lg bg-amber-500/30 hidden sm:flex items-center justify-center"><Clock className="w-4 h-4 text-amber-200" /></div>
          <div className="flex-1 min-w-0 basis-full sm:basis-auto order-last sm:order-none sm:mr-2">
            <h1 className="text-base sm:text-lg font-bold truncate">Timesheets</h1>
            <p className="hidden sm:block text-[11px] text-white/60">Log what you worked on, slide to set the hours, see your week or month at a glance.</p>
          </div>
          {isManager && <Button variant="outline" size="sm" className="h-9 bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white hidden sm:inline-flex" onClick={() => navigate('/staff/attendance')} data-testid="ts-open-attendance">Clock in/out report →</Button>}
          {isManager && (
            <select value={personId} onChange={(e) => setPersonId(e.target.value)} className="h-9 rounded-md bg-gray-800 border border-white/30 text-sm px-2 text-white max-w-[9rem] sm:max-w-none ml-auto sm:ml-0" data-testid="ts-person-filter">
              <option value="">My time</option>
              <option value="all">Everyone</option>
              {people.filter((p) => p.user_id !== user?.user_id).map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
            </select>
          )}
          {view === 'day' && personId !== 'all' && (
            <Button variant="outline" onClick={() => copyPrev(iso(anchor))} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="ts-copy-yesterday-header" title="Copy yesterday's entries onto this day">
              <CopyPlus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Same as yesterday</span>
            </Button>
          )}
          <Button onClick={() => setDialog({ date: view === 'day' ? iso(anchor) : todayIso() })} className="bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold" data-testid="ts-log-time">
            <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Log time</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 space-y-3">
        {(!isManager || !personId) && can('clock_in') && <ClockCard onSaved={load} />}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg bg-white border p-0.5" role="tablist">
            {VIEWS.map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`ts-view-${k}`}
                className={`px-3 py-1.5 text-sm font-bold rounded-md ${view === k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-1 ml-auto sm:ml-0">
            <Button variant="outline" size="icon" onClick={() => step(-1)} aria-label="Previous" data-testid="ts-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="ts-today">Today</Button>
            <Button variant="outline" size="icon" onClick={() => step(1)} aria-label="Next" data-testid="ts-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div className="font-bold text-gray-900 w-full sm:w-auto" data-testid="ts-range-title">{title}</div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_300px] gap-3 items-start">
          <div className="min-w-0">
            {view === 'day' && <DayView anchor={anchor} entries={entries} onAdd={(d) => setDialog({ date: d })} onOpen={(e) => setDialog({ entry: e, date: e.date })} showPerson={showPerson} onCopyPrev={personId === 'all' ? null : copyPrev} />}
            {view === 'week' && <WeekView anchor={anchor} entries={entries} onAdd={(d) => setDialog({ date: d })} onOpen={(e) => setDialog({ entry: e, date: e.date })} showPerson={showPerson} />}
            {view === 'month' && <MonthView anchor={anchor} entries={entries} onPickDay={(d) => { setAnchor(d); setView('day'); }} onAdd={(d) => setDialog({ date: d })} />}
          </div>
          <div className="space-y-3 min-w-0">
            <SummaryPanel summary={summary} showPeople={showPerson} exportUrl={`${API_URL}/api/timesheets/export.csv?${qs}`} />
            {isManager && <WhosIn />}
            {isManager && <WorkLocations />}
            {isManager && <JobTypeManager jobTypes={jobTypes} onChanged={loadStatic} />}
            {isManager && <HoursDigestCard />}
          </div>
        </div>
      </div>

      <EntryDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        entry={dialog?.entry}
        date={dialog?.date || todayIso()}
        jobTypes={jobTypes}
        people={people}
        canPickPerson={isManager}
        defaultUserId={dialog?.entry?.user_id || (personId && personId !== 'all' ? personId : user?.user_id)}
        onSaved={onSaved}
        onDeleted={onDeleted}
      />
    </div>
  );
}
